import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { Center } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import { FCWithChildren } from 'common/types';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { PaymentCardType } from 'types/creditCard';

// Shared state
interface RequestNewCardSharedState {
  bankAccountId: string;
  cardType?: PaymentCardType;
  companyProfileId: string;
  companyUserId: string;
  pinCode: string;
  budgetCardAmount: number;
  budgetCardDescription: string;
  pinCodeConfirm: string;
  isPasscodeShown: boolean;
  isBudgetCard: boolean;
}

// Context
interface RequestNewCardContext {
  sharedState: RequestNewCardSharedState;
  setSharedState: Dispatch<SetStateAction<RequestNewCardSharedState>>;
}

const REQUEST_NEW_CARD_CONTEXT = createContext<
  RequestNewCardContext | undefined
>(undefined);

export const RequestNewCardContextProvider: FCWithChildren = ({ children }) => {
  const { companyProfileId = '', companyUserId = '' } = useCompanyContext();
  const { error, loading, mainBankAccountId = '' } = useMainBankAccountId();

  const [sharedState, setSharedState] = useState({
    bankAccountId: mainBankAccountId,
    budgetCardAmount: 0,
    budgetCardDescription: '',
    companyProfileId,
    companyUserId,
    isBudgetCard: false,
    isPasscodeShown: false,
    pinCode: '',
    pinCodeConfirm: '',
  });

  if (loading) {
    return (
      <Center height="100%">
        <Loader />
      </Center>
    );
  }

  if (error || !companyProfileId || !mainBankAccountId) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  return (
    <REQUEST_NEW_CARD_CONTEXT.Provider value={{ setSharedState, sharedState }}>
      {children}
    </REQUEST_NEW_CARD_CONTEXT.Provider>
  );
};

// Hook
export const useRequestNewCardContext = () => {
  const context = useContext(REQUEST_NEW_CARD_CONTEXT);

  if (!context) {
    throw new Error(
      '"useRequestNewCardContext" can only be used under RequestNewCardContextProvider',
    );
  }

  return context;
};
